import { Injectable } from '@angular/core';
import { find, some, toString } from 'lodash';

import {
  ClientContactCreateModel,
  ClientCreateModel,
} from '@lockton-apps/client-gateway/interfaces';
import {
  Client,
  ClientContactCreate,
  ClientCreate,
  ClientType,
  ClientUpdate,
  T2Code,
} from '@lockton-apps/shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class ClientsTransformationService {
  clientCreate(
    model: ClientCreateModel,
    contactModel: ClientContactCreateModel
  ): ClientCreate {
    const { clientType } = model;
    if (clientType === ClientType.Individual) {
      return this._clientCreateIndividual(model, contactModel);
    }
    return this._clientCreateCompany(model, contactModel);
  }

  clientContact(model: ClientContactCreateModel): ClientContactCreate {
    return {
      email: model.email,
      givenName: model.givenName,
      surname: model.surname,
      phoneNumber: model.phoneNumber || '',
      policyIds: model.policyIds || [],
      isPrimary: true,
      language: model.language,
      displayName: `${model.givenName} ${model.surname}`,
    };
  }

  private _clientCreateIndividual(
    m: ClientCreateModel,
    contactM: ClientContactCreateModel
  ): ClientCreate {
    const { clientType } = m;
    const { address } = contactM;

    if (clientType !== ClientType.Individual) {
      throw new Error('Client type error');
    }
    return {
      locktonId: m.locktonId,
      singleClientId: m.singleClientId || null,
      clientType,
      classOfBusinessId: m.classOfBusinessId || 1, // #TODO - API issue This needs to be optional
      relatesToClientId: null,
      relationshipManagerUserId: null,
      customRef1: m.practiceCode,
      additionalFields: {
        PracticeCode: m.practiceCode,
      },
      clientData: {
        insuredName: `${contactM.givenName} ${contactM.surname}`,
        industrySector: m.industrySector,
        financialClassification: m.financialClassification,
        phoneNumber: contactM.phoneNumber || '0888888888', // #TODO phone number on clients
        email: contactM.email,
        ...address,
      },
    };
  }

  private _clientCreateCompany(
    m: ClientCreateModel,
    contactM: ClientContactCreateModel
  ): ClientCreate {
    const { clientType, address } = m;

    if (clientType !== ClientType.Company) {
      throw new Error('Client type missing');
    }

    return {
      locktonId: m.locktonId,
      singleClientId: m.singleClientId || null,
      clientType,
      classOfBusinessId: m.classOfBusinessId,
      relatesToClientId: null, // This is done seperate at a later stage for sub clients
      relationshipManagerUserId: null,
      customRef1: m.practiceCode,
      additionalFields: {
        PracticeCode: m.practiceCode,
      },
      clientData: {
        insuredName: m.insuredName,
        industrySector: m.industrySector,
        financialClassification: m.financialClassification,
        phoneNumber: contactM.phoneNumber || '07777777777', // #TODO phone number on clients
        email: contactM.email || null,
        ...address,
      },
    };
  }

  clientSubClient(m: ClientCreateModel) {
    const { clientType, address } = m;

    if (clientType !== ClientType.Company) {
      throw new Error('Client type missing');
    }

    return {
      locktonId: m.locktonId,
      singleClientId: m.singleClientId || null,
      clientType,
      classOfBusinessId: m.classOfBusinessId,
      relatesToClientId: m.relatesToClientId || null, // This is done seperate at a later stage for sub clients
      relationshipManagerUserId: null,
      customRef1: m.practiceCode,
      additionalFields: {
        PracticeCode: m.practiceCode,
      },
      clientData: {
        insuredName: m.insuredName,
        industrySector: m.industrySector,
        financialClassification: m.financialClassification,
        phoneNumber: m.phoneNumber, // #TODO phone number on clients
        email: m.email,
        ...address,
      },
    };
  }

  clientUpdate(clientModel: ClientCreateModel, clientId: number): ClientUpdate {
    const { clientType, address } = clientModel;

    if (
      !(
        clientType === ClientType.Company ||
        clientType === ClientType.Individual
      )
    ) {
      throw new Error('Client type missing');
    }

    return {
      id: clientId,
      clientType: clientType,
      locktonId: clientModel.locktonId,
      singleClientId: clientModel.singleClientId || null,
      relationshipManagerUserId: null,
      relatesToClientId: clientModel.relatesToClientId,
      classOfBusinessId: clientModel.classOfBusinessId,
      customRef1: clientModel.practiceCode,
      additionalFields: {
        PracticeCode: clientModel.practiceCode,
        ...clientModel.additionalFields,
      },
      clientData: {
        insuredName: clientModel.insuredName,
        phoneNumber: clientModel.phoneNumber,
        industrySector: clientModel.industrySector,
        financialClassification: clientModel.financialClassification,
        email: clientModel.email,
        ...address,
      },
    };
  }

  clientModel(client: Client, t2Codes: T2Code[]): ClientCreateModel {
    const address = {
      street: client.clientData.street,
      cityTown: client.clientData.cityTown,
      country: client.clientData.country,
      postcode: client.clientData.postcode,
    };

    const department =
      find(t2Codes, (t2Code) =>
        some(
          t2Code.classOfBusinesses,
          (cob) => cob.id === client.classOfBusinessId
        )
      ) || null;

    return {
      clientType: client.clientType,
      locktonId: toString(client.locktonId),
      singleClientId: toString(client.singleClientId),
      insuredName: client.clientData.insuredName,
      address,
      additionalFields: client.additionalFields,
      industrySector: client.clientData.industrySector,
      financialClassification: client.clientData.financialClassification,
      department: department ? department.id : null,
      practiceCode: client.customRef1,
      classOfBusinessId: client.classOfBusinessId,
      email: client.clientData.email,
      phoneNumber: client.clientData.phoneNumber,
      relatesToClientId: client.relatesToClientId,
    };
  }
}
